import React from "react";
import { ArrowLeftRight, Undo2, Bot, FastForward } from "lucide-react";
import { HardwareButton, SectionLabel, Knob } from "../../Components/Hardware";

function DeckLevelGauge({ side, percent, active }) {
  const isA = side === "A";
  const barColor = isA ? "#60a5fa" : "#f472b6";
  const dotColorClass = isA ? "bg-blue-400" : "bg-pink-400";
  const labelColorClass = isA ? "text-blue-300" : "text-pink-300";
  const accentLabel = isA ? "text-blue-400" : "text-pink-400";
  return (
    <div className="flex flex-col items-center gap-1.5">
      <span className={`text-[9px] font-mono uppercase tracking-[0.2em] ${accentLabel}`}>{side}</span>
      <div className="relative h-[88px] w-[10px] bg-black border border-neutral-800 rounded-sm overflow-hidden shadow-inner">
        <div
          className="absolute bottom-0 left-0 right-0"
          style={{
            height: `${percent}%`,
            background: `linear-gradient(to top, ${barColor}, ${barColor}aa 60%, ${barColor}55)`,
            boxShadow: active ? `0 0 8px ${barColor}` : undefined,
            transition: "height 60ms linear",
          }}
        />
        {[20, 40, 60, 80].map((t) => (
          <div
            key={t}
            className="absolute left-0 right-0 h-px bg-neutral-800"
            style={{ bottom: `${t}%` }}
          />
        ))}
      </div>
      <div className="flex items-center gap-1">
        <span
          className={`w-1.5 h-1.5 rounded-full ${active ? dotColorClass : "bg-neutral-700"}`}
          style={active ? { boxShadow: `0 0 6px ${barColor}` } : undefined}
        />
        <span className={`text-[10px] font-mono ${labelColorClass} tabular-nums w-8 text-right`}>
          {percent}%
        </span>
      </div>
    </div>
  );
}

export default function MasterControls({ crossfade, setCrossfade, autoTransition, transitionFading, onAutoTransition, onRevertTransition, onAccelerateTransition, queueMode, onToggleQueueMode, queueLength, transitionAvailable }) {
  const transitionTone = transitionFading ? "warn" : "go";
  const transitionTitle = transitionFading
    ? "Revert transition — restore previous deck and pause incoming"
    : queueMode
      ? "Trigger the upcoming transition now"
      : autoTransition
        ? "Cancel transition"
        : "Start a single transition now";
  const onTransitionClick = transitionFading ? onRevertTransition : onAutoTransition;
  const percentToA = Math.round((1 - crossfade) * 100);
  const percentToB = 100 - percentToA;

  return (
    <div className="px-4 pt-3 pb-3 flex flex-col items-center gap-2">
      <div className="flex items-end justify-center gap-6">
        <DeckLevelGauge side="A" percent={percentToA} active={crossfade < 0.5} />
        <DeckLevelGauge side="B" percent={percentToB} active={crossfade >= 0.5} />
      </div>

      <div className="relative" style={{ width: 180, height: 220 }}>
        <div className="absolute left-1/2 top-[72px] -translate-x-1/2 -translate-y-1/2">
          <Knob
            value={crossfade}
            onChange={setCrossfade}
            size={144}
            ariaLabel="Crossfader"
          />
        </div>

        <div
          className="absolute flex flex-col items-center gap-1"
          style={{ left: "50%", top: "143px", transform: "translate(calc(-50% + 70px), 0)" }}
        >
          <HardwareButton
            round
            onClick={onTransitionClick}
            disabled={!transitionAvailable && !transitionFading}
            tone={transitionTone}
            title={transitionTitle}
          >
            {transitionFading ? <Undo2 className="w-5 h-5" /> : <ArrowLeftRight className="w-5 h-5" />}
          </HardwareButton>
          <SectionLabel>Transition</SectionLabel>
        </div>

        <div
          className="absolute flex flex-col items-center gap-1"
          style={{ left: "50%", top: "143px", transform: "translate(calc(-50% - 70px), 0)" }}
        >
          <HardwareButton
            round
            onClick={onToggleQueueMode}
            disabled={!queueLength}
            tone={queueMode ? "stop" : "queue"}
            title={
              !queueLength
                ? "Load a playlist on the right to enable"
                : queueMode
                  ? "Stop Auto-DJ"
                  : "Start Auto-DJ — auto-mix through the current playlist"
            }
          >
            <Bot className={`w-5 h-5 ${queueMode ? "animate-pulse" : ""}`} />
          </HardwareButton>
          <SectionLabel>Auto DJ</SectionLabel>
        </div>

        <div
          className="absolute"
          style={{ left: "50%", top: "167px", transform: "translate(-50%, -50%)" }}
        >
          <SectionLabel>Crossfader</SectionLabel>
        </div>

        {transitionFading && (
          <div
            className="absolute"
            style={{ left: "50%", top: "72px", transform: "translate(-50%, calc(-50% + 135px))" }}
          >
            <HardwareButton
              round
              onClick={onAccelerateTransition}
              tone="go"
              title="Accelerate transition — each press halves the remaining time"
            >
              <FastForward className="w-5 h-5" />
            </HardwareButton>
          </div>
        )}
      </div>
    </div>
  );
}
